import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react"
import { IoClose, IoCloseOutline } from "react-icons/io5"

import BlurWrapper from "@fuse/sharedComponents/blurWrapper"

export interface BottomSheetHandle {
  openModal: () => void
  closeModal: () => void
}

interface BottomSheetProps {
  title: string
  titleIcon?: React.ReactNode
  children: React.ReactNode
  isDefaultBg?: boolean
  maxWidth?: "sm" | "md" | "lg" | "xl" | "2xl"
  height?: string
  onOpen?: () => void
  onClose?: () => void
  zIndex?: number
  shouldCloseOnCloseButton?: boolean
  className?: string
  showHeader?: boolean
}

const BottomSheet = forwardRef<BottomSheetHandle, BottomSheetProps>(
  (
    {
      title,
      titleIcon,
      children,
      isDefaultBg = false,
      showHeader = true,
      maxWidth = "sm",
      height = "h-[45vh] max-h-[45vh]",
      onOpen,
      onClose,
      zIndex,
      shouldCloseOnCloseButton = true,
      className,
    },
    ref,
  ) => {
    const [isVisible, setIsVisible] = useState(false)

    const handleOpen = () => {
      setIsVisible(true)
      onOpen?.()
    }

    const handleClose = () => {
      if (shouldCloseOnCloseButton) {
        setIsVisible(false)
      }
      onClose?.()
    }

    // Handle back button for bottom sheet
    useEffect(() => {
      if (isVisible) {
        const modalId = `bottomsheet-${Date.now()}`

        // Add a dummy entry to history when bottom sheet opens
        window.history.pushState(
          { bottomSheetOpen: true, modalId },
          "",
          window.location.href,
        )

        // Listen for back button press with higher priority (capture phase)
        const handlePopState = (event: PopStateEvent) => {
          // Close modal when navigating back from our modal state
          if (!event.state?.bottomSheetOpen) {
            event.stopImmediatePropagation() // Prevent other handlers
            handleClose()
          }
        }

        window.addEventListener("popstate", handlePopState, true) // Use capture phase

        return () => {
          window.removeEventListener("popstate", handlePopState, true)
        }
      }
    }, [isVisible])

    // Clean up history when modal closes normally (via X button)
    useEffect(() => {
      if (!isVisible && window.history.state?.bottomSheetOpen) {
        // Replace current state to remove modal indicator instead of going back
        window.history.replaceState(
          { ...window.history.state, bottomSheetOpen: false },
          "",
          window.location.href,
        )
      }
    }, [isVisible])

    useImperativeHandle(ref, () => ({
      openModal: handleOpen,
      closeModal: handleClose,
    }))

    if (!isVisible) return null

    const maxWidthClasses = {
      sm: "md:max-w-sm",
      md: "md:max-w-md",
      lg: "md:max-w-lg",
      xl: "md:max-w-xl",
      "2xl": "md:max-w-2xl",
    }

    return (
      <div
        className={`fixed inset-0 flex justify-center bg-black bg-opacity-50 items-end md:items-center place-items-center`}
        style={{ zIndex: zIndex || 100 }}
        onClick={handleClose}
      >
        <BlurWrapper
          overlayOpacityDefault="20"
          className={`${className} w-full overflow-hidden ${maxWidthClasses[maxWidth]} ${
            isDefaultBg ? "!bg-black/50" : "!bg-gray-500/10"
          } rounded-t-2xl md:rounded-2xl p-6 relative flex flex-col cursor-auto ${height} gap-4`}
          onClick={(e) => e.stopPropagation()}
        >
          {showHeader && (
            <div className="relative flex items-center justify-center">
              <div className="text-white/70 text-base font-medium text-center w-full flex items-center justify-center gap-2 ">
                {titleIcon && titleIcon}
                {title}
              </div>
              <div className="absolute right-0" onClick={handleClose}>
                <IoClose color="#fff" size={28} />
              </div>
            </div>
          )}
          <div className="overflow-y-auto overflow-x-hidden no-scrollbar">
            {children}
          </div>
        </BlurWrapper>
      </div>
    )
  },
)

export default BottomSheet
